import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import HypervisorPingGraph from '../components/HypervisorPingGraph';
import api from '../utils/api';
import { toast } from 'sonner';
import { Server, Plus, Loader2, Trash2, RefreshCw, CheckCircle2, XCircle, AlertCircle, Eye, EyeOff, Edit, Activity } from 'lucide-react';

const HypervisorMonitoring = () => {
  const navigate = useNavigate();
  const [hypervisors, setHypervisors] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showMetricsDialog, setShowMetricsDialog] = useState(false);
  const [editingHypervisor, setEditingHypervisor] = useState(null);
  const [selectedHypervisor, setSelectedHypervisor] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [hypervisorToDelete, setHypervisorToDelete] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [name, setName] = useState('');
  const [hypervisorType, setHypervisorType] = useState('proxmox');
  const [hostUrl, setHostUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [port, setPort] = useState('');
  const [sslVerify, setSslVerify] = useState(false);
  const [clientId, setClientId] = useState('none');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [hypervisorsRes, clientsRes] = await Promise.all([
        api.get('/hypervisors'),
        api.get('/clients')
      ]);
      setHypervisors(hypervisorsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setHypervisorType('proxmox');
    setHostUrl('');
    setUsername('');
    setPassword('');
    setPort('');
    setSslVerify(false);
    setClientId('none');
    setShowPassword(false);
  };

  const handleAddHypervisor = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const hypervisorData = {
        name,
        hypervisor_type: hypervisorType,
        host_url: hostUrl,
        username,
        password,
        client_id: clientId && clientId !== 'none' ? clientId : undefined,
        ssl_verify: sslVerify
      };

      if (port) {
        hypervisorData.port = parseInt(port);
      }

      await api.post('/hypervisors', hypervisorData);
      toast.success('Hypervisor added successfully!');
      setShowAddDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add hypervisor');
    } finally {
      setSaving(false);
    }
  };

  const handleEditHypervisor = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const updateData = {
        name,
        host_url: hostUrl,
        username,
        client_id: clientId && clientId !== 'none' ? clientId : undefined,
        ssl_verify: sslVerify
      };

      if (password) {
        updateData.password = password;
      }

      if (port) {
        updateData.port = parseInt(port);
      }

      await api.put(`/hypervisors/${editingHypervisor.id}`, updateData);
      toast.success('Hypervisor updated successfully!');
      setShowEditDialog(false);
      setEditingHypervisor(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update hypervisor');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (hypervisorId) => {
    setTesting(prev => ({ ...prev, [hypervisorId]: true }));

    try {
      const response = await api.post(`/hypervisors/${hypervisorId}/test`);
      if (response.data.status === 'success') {
        toast.success(`Connection successful! ${response.data.message}`);
      } else {
        toast.error(`Connection failed: ${response.data.message}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(prev => ({ ...prev, [hypervisorId]: false }));
    }
  };

  const handleViewMetrics = async (hypervisor) => {
    setSelectedHypervisor(hypervisor);
    setShowMetricsDialog(true);
    setLoadingMetrics(true);
    setMetrics(null);

    try {
      const response = await api.get(`/hypervisors/${hypervisor.id}/metrics`);
      setMetrics(response.data);
    } catch (error) {
      toast.error('Failed to load metrics');
      setShowMetricsDialog(false);
    } finally {
      setLoadingMetrics(false);
    }
  };

  const openEditDialog = (hypervisor) => {
    setEditingHypervisor(hypervisor);
    setName(hypervisor.name);
    setHypervisorType(hypervisor.hypervisor_type);
    setHostUrl(hypervisor.host_url);
    setUsername(hypervisor.username);
    setPassword('');
    setPort(hypervisor.port?.toString() || '');
    setSslVerify(hypervisor.ssl_verify);
    setClientId(hypervisor.client_id || 'none');
    setShowEditDialog(true);
  };

  const handleDeleteClick = (hypervisor) => {
    setHypervisorToDelete(hypervisor);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!hypervisorToDelete) return;

    try {
      await api.delete(`/hypervisors/${hypervisorToDelete.id}`);
      toast.success('Hypervisor deleted successfully!');
      setDeleteDialogOpen(false);
      setHypervisorToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete hypervisor');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      online: { variant: 'default', icon: CheckCircle2, label: 'Online', className: 'bg-green-500' },
      offline: { variant: 'destructive', icon: XCircle, label: 'Offline' },
      error: { variant: 'destructive', icon: AlertCircle, label: 'Error' },
      unknown: { variant: 'secondary', icon: AlertCircle, label: 'Unknown' }
    };

    const config = statusConfig[status] || statusConfig.unknown;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatBytes = (gb) => {
    if (gb >= 1024) return `${(gb / 1024).toFixed(2)} TB`;
    return `${gb.toFixed(2)} GB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Server className="w-8 h-8 text-blue-600" />
            Hypervisor Monitoring
          </h1>
          <p className="text-slate-600 mt-1 text-sm">
            Monitor ESXi and Proxmox hypervisors
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Hypervisor
        </Button>
      </div>

      {hypervisors.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Server className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No hypervisors configured</h3>
            <p className="text-slate-500 text-center mb-6 max-w-md">
              Get started by adding your first ESXi or Proxmox hypervisor to monitor VMs, CPU, memory, and storage.
            </p>
            <Button onClick={() => setShowAddDialog(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Hypervisor
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {hypervisors.map((hypervisor) => (
            <Card key={hypervisor.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Server className="w-5 h-5 text-blue-600" />
                    <CardTitle className="text-lg">{hypervisor.name}</CardTitle>
                  </div>
                  {getStatusBadge(hypervisor.status)}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className="text-xs">
                    {hypervisor.hypervisor_type === 'proxmox' ? 'Proxmox' : 'ESXi'}
                  </Badge>
                  {hypervisor.client_id && (
                    <Badge variant="secondary" className="text-xs">
                      {clients.find(c => c.id === hypervisor.client_id)?.company_name || 'Client'}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Host:</span>
                    <span className="font-mono text-xs">{hypervisor.host_url}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Username:</span>
                    <span className="font-mono text-xs">{hypervisor.username}</span>
                  </div>
                  {hypervisor.last_check && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Last Check:</span>
                      <span className="text-xs">{new Date(hypervisor.last_check).toLocaleString()}</span>
                    </div>
                  )}
                </div>

                {/* Ping Graph */}
                <HypervisorPingGraph hypervisorId={hypervisor.id} compact={true} />

                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewMetrics(hypervisor)}
                    className="flex-1"
                  >
                    <Activity className="w-4 h-4 mr-1" />
                    Metrics
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestConnection(hypervisor.id)}
                    disabled={testing[hypervisor.id]}
                  >
                    {testing[hypervisor.id] ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(hypervisor)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteClick(hypervisor)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Hypervisor Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Hypervisor</DialogTitle>
            <DialogDescription>
              Configure a new ESXi or Proxmox hypervisor connection
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddHypervisor}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Production ESXi Host"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="hypervisor-type">Hypervisor Type *</Label>
                <Select value={hypervisorType} onValueChange={setHypervisorType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="proxmox">Proxmox VE</SelectItem>
                    <SelectItem value="esxi">VMware ESXi / vCenter</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="host-url">Host URL *</Label>
                <Input
                  id="host-url"
                  value={hostUrl}
                  onChange={(e) => setHostUrl(e.target.value)}
                  placeholder={hypervisorType === 'proxmox' ? 'https://proxmox.example.com' : 'https://vcenter.example.com'}
                  required
                />
                <p className="text-xs text-slate-500">
                  {hypervisorType === 'proxmox' 
                    ? 'Enter your Proxmox host URL (default port: 8006)' 
                    : 'Enter your ESXi/vCenter host URL (default port: 443)'}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="username">Username *</Label>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={hypervisorType === 'proxmox' ? 'root@pam' : 'administrator@vsphere.local'}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="port">Port (Optional)</Label>
                <Input
                  id="port"
                  type="number"
                  value={port}
                  onChange={(e) => setPort(e.target.value)}
                  placeholder={hypervisorType === 'proxmox' ? '8006' : '443'}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client">Client (Optional)</Label>
                <Select value={clientId} onValueChange={setClientId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Client</SelectItem>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="ssl-verify"
                  checked={sslVerify}
                  onCheckedChange={setSslVerify}
                />
                <Label htmlFor="ssl-verify" className="font-normal">
                  Verify SSL Certificate
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Add Hypervisor
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Hypervisor Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Hypervisor</DialogTitle>
            <DialogDescription>
              Update hypervisor connection settings
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditHypervisor}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name *</Label>
                <Input
                  id="edit-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Hypervisor Type</Label>
                <Input
                  value={hypervisorType === 'proxmox' ? 'Proxmox VE' : 'VMware ESXi'}
                  disabled
                  className="bg-slate-100"
                />
                <p className="text-xs text-slate-500">Type cannot be changed after creation</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-host-url">Host URL *</Label>
                <Input
                  id="edit-host-url"
                  value={hostUrl}
                  onChange={(e) => setHostUrl(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-username">Username *</Label>
                <Input
                  id="edit-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-password">Password (leave blank to keep current)</Label>
                <div className="relative">
                  <Input
                    id="edit-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password to update"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-port">Port (Optional)</Label>
                <Input
                  id="edit-port"
                  type="number"
                  value={port}
                  onChange={(e) => setPort(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-client">Client (Optional)</Label>
                <Select value={clientId} onValueChange={setClientId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Client</SelectItem>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-ssl-verify"
                  checked={sslVerify}
                  onCheckedChange={setSslVerify}
                />
                <Label htmlFor="edit-ssl-verify" className="font-normal">
                  Verify SSL Certificate
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Update Hypervisor
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Metrics Dialog */}
      <Dialog open={showMetricsDialog} onOpenChange={setShowMetricsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Hypervisor Metrics - {selectedHypervisor?.name}</DialogTitle>
            <DialogDescription>
              Real-time metrics for {selectedHypervisor?.hypervisor_type === 'proxmox' ? 'Proxmox' : 'ESXi'} hypervisor
            </DialogDescription>
          </DialogHeader>

          {loadingMetrics ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : metrics ? (
            <div className="space-y-4">
              {/* Ping Graph */}
              {selectedHypervisor && (
                <HypervisorPingGraph hypervisorId={selectedHypervisor.id} compact={false} />
              )}

              {metrics.metrics.map((node, idx) => (
                <Card key={idx}>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Server className="w-5 h-5 text-blue-600" />
                      {node.node || node.host}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        Status: {node.status}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        Uptime: {formatUptime(node.uptime)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <div className="text-sm text-slate-600 mb-1">CPU Usage</div>
                        <div className="text-2xl font-bold text-slate-800">{node.cpu_usage}%</div>
                        <div className="text-xs text-slate-500 mt-1">
                          {node.max_cpu} {metrics.hypervisor_type === 'proxmox' ? 'cores' : 'cores'}
                        </div>
                      </div>
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <div className="text-sm text-slate-600 mb-1">Memory</div>
                        <div className="text-2xl font-bold text-slate-800">{node.memory_percentage}%</div>
                        <div className="text-xs text-slate-500 mt-1">
                          {formatBytes(node.memory_used)} / {formatBytes(node.memory_total)}
                        </div>
                      </div>
                      {node.storage_total !== undefined && (
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <div className="text-sm text-slate-600 mb-1">Storage</div>
                          <div className="text-2xl font-bold text-slate-800">{node.storage_percentage}%</div>
                          <div className="text-xs text-slate-500 mt-1">
                            {formatBytes(node.storage_used)} / {formatBytes(node.storage_total)}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Datastores / Storage */}
                    {node.datastores && node.datastores.length > 0 && (
                      <div className="mt-6">
                        <h4 className="font-semibold text-slate-800 mb-3">Datastores ({node.datastores.length})</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {node.datastores.map((ds, dsIdx) => (
                            <div key={dsIdx} className="bg-slate-50 border rounded-lg p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-sm">{ds.name}</span>
                                <Badge variant="outline" className="text-xs">{ds.type}</Badge>
                              </div>
                              <div className="space-y-1">
                                <div className="flex justify-between text-xs">
                                  <span className="text-slate-600">Used:</span>
                                  <span className="font-medium">{ds.used_gb || ds.usage_percentage}%</span>
                                </div>
                                <div className="w-full bg-slate-200 rounded-full h-2">
                                  <div 
                                    className={`h-2 rounded-full ${ds.usage_percentage > 90 ? 'bg-red-500' : ds.usage_percentage > 75 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                    style={{ width: `${ds.usage_percentage}%` }}
                                  ></div>
                                </div>
                                <div className="flex justify-between text-xs text-slate-500">
                                  <span>{ds.used_gb?.toFixed(2) || 0} GB used</span>
                                  <span>{ds.total_gb?.toFixed(2) || ds.capacity_gb?.toFixed(2) || 0} GB total</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Networks */}
                    {node.networks && node.networks.length > 0 && (
                      <div className="mt-6">
                        <h4 className="font-semibold text-slate-800 mb-3">Network Adapters ({node.networks.length})</h4>
                        <div className="flex flex-wrap gap-2">
                          {node.networks.map((net, netIdx) => (
                            <Badge key={netIdx} variant="outline" className="text-xs">
                              {net.name} ({net.type})
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Hardware Info */}
                    {(node.vendor || node.model) && (
                      <div className="mt-6">
                        <h4 className="font-semibold text-slate-800 mb-3">Hardware Information</h4>
                        <div className="bg-slate-50 border rounded-lg p-3 text-sm">
                          {node.vendor && <div><span className="text-slate-600">Vendor:</span> <span className="font-medium">{node.vendor}</span></div>}
                          {node.model && <div><span className="text-slate-600">Model:</span> <span className="font-medium">{node.model}</span></div>}
                          {node.cpu_mhz && <div><span className="text-slate-600">CPU Speed:</span> <span className="font-medium">{node.cpu_mhz} MHz</span></div>}
                        </div>
                      </div>
                    )}

                    <div className="mt-6">
                      <h4 className="font-semibold text-slate-800 mb-3">Virtual Machines ({node.vms?.length || 0})</h4>
                      {node.vms && node.vms.length > 0 ? (
                        <div className="space-y-2">
                          {node.vms.map((vm, vmIdx) => (
                            <div key={vmIdx} className="bg-white border rounded-lg p-3">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-slate-800">{vm.name}</span>
                                  <Badge variant={vm.status === 'running' || vm.status === 'poweredOn' ? 'default' : 'secondary'} className="text-xs">
                                    {vm.status}
                                  </Badge>
                                </div>
                                {vm.vmid && (
                                  <span className="text-xs text-slate-500">ID: {vm.vmid}</span>
                                )}
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-slate-600">
                                <div>
                                  CPU: {vm.cpu ? `${vm.cpu}` : `${vm.maxcpu || 0} cores`}
                                </div>
                                <div>
                                  RAM: {formatBytes(vm.mem || vm.maxmem || 0)}
                                </div>
                                {vm.disk !== undefined && (
                                  <div>
                                    Disk: {formatBytes(vm.disk || 0)}
                                  </div>
                                )}
                                {vm.uptime > 0 && (
                                  <div>
                                    Uptime: {formatUptime(vm.uptime)}
                                  </div>
                                )}
                                {vm.guest_os && (
                                  <div className="col-span-2">
                                    OS: {vm.guest_os}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500">No VMs found</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              Failed to load metrics
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMetricsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Hypervisor</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{hypervisorToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HypervisorMonitoring;
